import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowUpRight } from 'lucide-react';
import Reveal from './motion/Reveal';

const shots = [
  {
    src: '/app-scan.jpg',
    alt: 'VerifAEye scanning multiple household items in one photo',
    caption: 'Multi-item smart scanning',
    detail: 'One photo, every item detected'
  },
  {
    src: '/app-value.jpg',
    alt: 'VerifAEye calculating estimated replacement cost of contents',
    caption: 'Live replacement values',
    detail: 'Estimated cost per item, automatically'
  },
  {
    src: '/app-receipt.jpg',
    alt: 'VerifAEye syncing receipts with inventory items',
    caption: 'Smart receipt sync',
    detail: 'Receipts matched to your inventory'
  }
];

const ProductShowcase = () => {
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start']
  });
  const yOuter = useTransform(scrollYProgress, [0, 1], [60, -60]);
  const yInner = useTransform(scrollYProgress, [0, 1], [120, -120]);

  return (
    <section
      id="showcase"
      data-testid="product-showcase"
      ref={sectionRef}
      className="relative bg-ink py-28 overflow-hidden"
    >
      <div className="absolute inset-0">
        <div className="absolute w-[40rem] h-[40rem] bg-cyan-500/10 rounded-full blur-[140px] top-1/3 left-1/2 -translate-x-1/2"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
        <Reveal>
          <div className="max-w-3xl mb-20">
            <p className="eyebrow text-cyan-400 mb-5">The first application</p>
            <h2 className="text-4xl lg:text-6xl font-bold text-white leading-tight">
              Meet <span className="bg-gradient-to-r from-cyan-400 to-emerald-400 bg-clip-text text-transparent">VerifAEye</span>
            </h2>
            <p className="mt-6 text-lg md:text-xl text-slate-400 leading-relaxed">
              Your smartphone becomes a forensic insurance assessor. When the worst happens, 
              you're ready to prove everything you own.
            </p>
          </div>
        </Reveal>

        {/* Clipped frames with spotlight + parallax */}
        <div className="grid md:grid-cols-3 gap-8 lg:gap-12 items-start">
          {shots.map((shot, i) => (
            <motion.div
              key={i}
              style={{ y: i === 1 ? yInner : yOuter }}
              className={i === 1 ? 'md:mt-16' : ''}
            >
              <Reveal delay={i * 0.12}>
                <div data-testid={`showcase-frame-${i}`} className="group relative">
                  {/* Spotlight */}
                  <div className="absolute -inset-8 bg-[radial-gradient(circle_at_50%_20%,rgba(34,211,238,0.18),transparent_70%)] blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none"></div>

                  {/* Clipped frame */}
                  <div className="relative rounded-[2rem] border border-white/10 bg-ink-soft p-2 shadow-2xl shadow-black/40 transition-transform duration-500 group-hover:-translate-y-2">
                    <div className="rounded-[1.6rem] overflow-hidden">
                      <img
                        src={shot.src}
                        alt={shot.alt}
                        loading="lazy"
                        className="w-full h-auto block transition-transform duration-700 group-hover:scale-[1.03]"
                      />
                    </div>
                    <div className="absolute inset-0 rounded-[2rem] ring-1 ring-inset ring-white/10 pointer-events-none"></div>
                  </div>

                  <div className="mt-6 flex items-start justify-between gap-4">
                    <div>
                      <p className="text-white font-semibold">{shot.caption}</p>
                      <p className="text-sm text-slate-500 mt-1">{shot.detail}</p>
                    </div>
                    <span className="eyebrow text-slate-600 shrink-0">0{i + 1}</span>
                  </div>
                </div>
              </Reveal>
            </motion.div>
          ))}
        </div>

        <Reveal delay={0.2}>
          <div className="mt-20 flex flex-col sm:flex-row items-center justify-between gap-6 border-t rule-dark pt-10">
            <p className="text-slate-400 text-lg max-w-xl">
              Document your entire home in less time than it takes to watch a TV show.
            </p>
            <a
              data-testid="showcase-cta"
              href="https://verifaeye.com.au"
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center gap-2 rounded-full border border-cyan-400/40 px-7 py-3.5 text-base font-semibold text-cyan-300 transition-colors duration-300 hover:bg-cyan-400/10"
            >
              Visit verifaeye.com.au
              <ArrowUpRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default ProductShowcase;
